"use client";

/**
 * AI Builder Dashboard
 * 
 * Main dashboard for managing all AIs in the Builder.
 * Shows all AIs with their types, spec sheets, connect/edit buttons, and AI redesign capability.
 */

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { 
  Plus, 
  Sparkles, 
  Trash2, 
  MessageSquare, 
  Link2, 
  ChevronDown,
  ChevronUp,
  Settings,
  Zap,
  Shield,
  Brain,
  Palette,
  ShoppingCart,
  Plug
} from "lucide-react";
import Link from "next/link";
import { getUserAIs, setActiveAI, deleteAIEntity, updateAIEntity } from "@/lib/ai/entityManager";
import type { AIEntity } from "@/types/aiEntity";
import { entityToAiProfile as convertEntityToProfile } from "@/types/aiEntity";
import { useUser } from "@/lib/user/context";
import { getUserId } from "@/lib/user/identity";
import BrandStyles from "@/components/branding/BrandStyles";
import UltraNavigationBarNavOnly from "@/components/chat/UltraNavigationBarNavOnly";
import PremiumHeroBackground from "@/components/ui/PremiumHeroBackground";
import AIBackgroundMotion from "@/components/ui/AIBackgroundMotion";
import { getTextColorForBackground, getSecondaryTextColorForBackground } from "@/lib/utils/colorContrast";
import { SUBSCRIPTION_PLANS } from "@/lib/platform/pricing";
import type { SubscriptionTier } from "@/lib/platform/types";

interface SpecSheetProps {
  ai: AIEntity;
  textColor: string;
  textSecondaryColor: string;
}

function SpecSheet({ ai, textColor, textSecondaryColor }: SpecSheetProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const modePresetLabels: Record<string, string> = {
    playful: "Playful",
    study: "Study",
    business: "Business"
  };

  const privacyModeLabels: Record<string, string> = {
    strict: "Strict",
    smart: "Smart"
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-sm font-medium mb-2"
        style={{ color: textColor }}
      >
        <span>Spec Sheet</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {isExpanded && (
        <div className="space-y-3 text-sm" style={{ color: textSecondaryColor }}>
          {/* AI Type */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Type:
            </span>
            <span className="font-medium" style={{ color: textColor }}>
              {modePresetLabels[ai.modePreset] || ai.modePreset}
            </span>
          </div>

          {/* Privacy Mode */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Privacy:
            </span>
            <span className="font-medium" style={{ color: textColor }}>
              {privacyModeLabels[ai.privacyMode] || ai.privacyMode}
            </span>
          </div>

          {/* Agent Mode */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Agent Mode:
            </span>
            <span className="font-medium" style={{ color: textColor }}>
              {ai.agentModeEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          {/* Plan */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Plan:
            </span>
            <span className="font-medium capitalize" style={{ color: textColor }}>
              {ai.plan || "free"}
            </span>
          </div>

          {/* Tiles */}
          {ai.tiles && ai.tiles.length > 0 && (
            <div>
              <span className="flex items-center gap-2 mb-2">
                <Palette className="w-4 h-4" />
                Personality Tiles:
              </span>
              <div className="flex flex-wrap gap-2">
                {ai.tiles.map((tile, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 rounded-md text-xs"
                    style={{
                      backgroundColor: `${ai.brand?.primaryColor || "#6366f1"}20`,
                      color: textColor,
                    }}
                  >
                    {tile}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sliders */}
          {ai.sliders && (
            <div className="space-y-2">
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Personality Sliders:
              </span>
              <div className="space-y-1 pl-6">
                {Object.entries(ai.sliders).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <span className="capitalize">{key}:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-white/10">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${value}%`,
                            backgroundColor: ai.brand?.primaryColor || "#6366f1",
                          }}
                        />
                      </div>
                      <span className="w-8 text-right">{value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Version & Metadata */}
          <div className="pt-2 border-t border-white/5 space-y-1 text-xs">
            <div className="flex justify-between">
              <span>Version:</span>
              <span className="font-medium">{ai.version || 1}</span>
            </div>
            <div className="flex justify-between">
              <span>Created:</span>
              <span>{new Date(ai.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span>Updated:</span>
              <span>{new Date(ai.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIBuilderDashboard() {
  const router = useRouter();
  const { user } = useUser();
  const [ais, setAIs] = useState<AIEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAIId, setActiveAIId] = useState<string | null>(null);
  const [expandedAIId, setExpandedAIId] = useState<string | null>(null);

  useEffect(() => {
    loadAIs();
  }, [user]);

  async function loadAIs() {
    // Use auth user id when available, otherwise local identity so AIs always load (same device)
    const userId = user?.id ?? (typeof window !== "undefined" ? getUserId() : undefined);
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const userAIs = await getUserAIs(userId);
      setAIs(userAIs);
      
      if (userAIs.length > 0) {
        const mostRecent = userAIs[0];
        setActiveAIId(mostRecent.id);
        await setActiveAI(mostRecent.id, userId);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load AIs";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleSwitchAI(ai: AIEntity) {
    const userId = user?.id ?? (typeof window !== "undefined" ? getUserId() : undefined);
    setActiveAIId(ai.id);
    await setActiveAI(ai.id, userId ?? undefined);
    router.push(`/chat?aiId=${ai.id}`);
  }

  async function handleDeleteAI(ai: AIEntity, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Delete "${ai.name}"? This cannot be undone.`)) return;
    
    const userId = user?.id ?? (typeof window !== "undefined" ? getUserId() : undefined);
    if (!userId) return;
    
    try {
      await deleteAIEntity(ai.id, userId);
      await loadAIs();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete AI";
      alert(`Failed to delete AI: ${errorMessage}`);
    }
  }

  function handleConnectToAI(ai: AIEntity, e: React.MouseEvent) {
    e.stopPropagation();
    // Check subscription for multi-agent workflows
    const userPlan: SubscriptionTier = user?.subscriptionTier ?? "free";
    const planFeatures = SUBSCRIPTION_PLANS[userPlan];
    
    if (!planFeatures?.features.multiAgentWorkflows) {
      alert("Multi-agent workflows require Pro plan or higher. Upgrade to connect AIs.");
      return;
    }
    
    // Open AI connection interface
    router.push(`/builder?aiId=${ai.id}&mode=connect`);
  }


  function handleNewAI() {
    router.push("/start");
  }

  function toggleExpand(aiId: string) {
    setExpandedAIId(expandedAIId === aiId ? null : aiId);
  }

  // Use first AI's brand colors for theming, or defaults
  const primaryColor = ais[0]?.brand?.primaryColor || "#6366f1";
  const secondaryColor = ais[0]?.brand?.secondaryColor || "#8b5cf6";
  const textColor = getTextColorForBackground(primaryColor);
  const textSecondaryColor = getSecondaryTextColorForBackground(primaryColor);

  if (loading) {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
          <div className="text-white">Loading your AIs...</div>
        </div>
      </PremiumHeroBackground>
    );
  }

  if (error && ais.length === 0) {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-6 max-w-md">
          <h2 className="text-xl font-bold text-white mb-2">Error Loading AIs</h2>
          <p className="text-red-200 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadAIs();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
          >
            Try Again
          </button>
        </div>
      </PremiumHeroBackground>
    );
  }

  return (
    <PremiumHeroBackground className="min-h-screen flex flex-col relative overflow-hidden">
      {ais[0] && <BrandStyles brand={ais[0].brand} />}

      {/* Animated background – motion type, direction, speed, randomness, intensity from brand */}
      <AIBackgroundMotion
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        motion={ais[0]?.brand?.backgroundMotion}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto relative z-10">
        <div style={{ maxWidth: '48rem', margin: '0 auto', padding: '2rem 1rem', minHeight: '100%' }}>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 
                  className="text-4xl md:text-5xl font-bold mb-2"
                  style={{ color: textColor }}
                >
                  AI Builder
                </h1>
                <p 
                  className="text-lg md:text-xl"
                  style={{ color: textSecondaryColor }}
                >
                  Manage, edit, and connect your AI assistants
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  href="/marketplace"
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold hover:from-purple-600 hover:to-pink-600 transition flex items-center gap-2"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Browse Marketplace
                </Link>
              </div>
            </div>
          </div>

          {/* AIs Grid */}
          {ais.length === 0 ? (
            <div className="flex items-center justify-center min-h-[50vh]">
              <div className="text-center max-w-md">
                <div 
                  className="premium-white-glass rounded-3xl p-8 md:p-10 mb-6"
                  style={{ 
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
                  }}
                >
                  <Sparkles 
                    className="w-16 h-16 mx-auto mb-6" 
                    style={{ color: textSecondaryColor }}
                  />
                  <h2 
                    className="text-2xl md:text-3xl font-semibold mb-3"
                    style={{ color: textColor }}
                  >
                    No AIs yet
                  </h2>
                  <p 
                    className="text-base md:text-lg mb-6"
                    style={{ color: textSecondaryColor }}
                  >
                    Create your first AI assistant to get started
                  </p>
                  <button
                    onClick={handleNewAI}
                    className="px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      color: getTextColorForBackground(primaryColor),
                      boxShadow: `0 4px 16px ${primaryColor}40`,
                    }}
                  >
                    Create Your First AI
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6 flex justify-end">
                <button
                  onClick={handleNewAI}
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    color: getTextColorForBackground(primaryColor),
                    boxShadow: `0 4px 16px ${primaryColor}40`,
                  }}
                >
                  <Plus className="w-5 h-5" />
                  New AI
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {ais.map((ai) => {
                  const isActive = activeAIId === ai.id;
                  const isExpanded = expandedAIId === ai.id;
                  const aiPrimaryColor = ai.brand?.primaryColor || primaryColor;
                  const aiSecondaryColor = ai.brand?.secondaryColor || secondaryColor;
                  // Get user subscription tier (default to free if not available)
                  const userPlan: SubscriptionTier = user?.subscriptionTier ?? "free";
                  const planFeatures = SUBSCRIPTION_PLANS[userPlan];
                  const canConnect = planFeatures?.features.multiAgentWorkflows || false;
                  
                  return (
                    <div
                      key={ai.id}
                      className={`premium-white-glass rounded-2xl p-5 md:p-6 transition-all duration-200 cursor-pointer hover:scale-[1.02] active:scale-[0.98] relative ${
                        isActive ? "ring-2" : ""
                      }`}
                      style={{
                        boxShadow: isActive 
                          ? `0 8px 32px ${aiPrimaryColor}30, inset 0 1px 0 rgba(255,255,255,0.6)`
                          : '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)',
                        ...(isActive && { borderColor: aiPrimaryColor }),
                      }}
                      onClick={() => toggleExpand(ai.id)}
                    >
                      {/* Active indicator */}
                      {isActive && (
                        <div 
                          className="absolute top-3 right-3 w-2 h-2 rounded-full"
                          style={{ backgroundColor: aiPrimaryColor }}
                        />
                      )}

                      {/* AI Info */}
                      <div className="mb-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                          style={{
                            background: `linear-gradient(135deg, ${aiPrimaryColor}20 0%, ${aiSecondaryColor}20 100%)`,
                          }}
                        >
                          <Sparkles 
                            className="w-6 h-6"
                            style={{ color: aiPrimaryColor }}
                          />
                        </div>
                        <h3 
                          className="font-semibold text-lg md:text-xl mb-1 line-clamp-1"
                          style={{ color: textColor }}
                        >
                          {ai.name}
                        </h3>
                        {ai.description && (
                          <p 
                            className="text-sm md:text-base line-clamp-2"
                            style={{ color: textSecondaryColor }}
                          >
                            {ai.description}
                          </p>
                        )}
                      </div>

                      {/* Spec Sheet */}
                      <SpecSheet 
                        ai={ai} 
                        textColor={textColor} 
                        textSecondaryColor={textSecondaryColor} 
                      />

                      {/* Action Buttons */}
                      <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(`/builder?aiId=${ai.id}&mode=build`);
                          }}
                          className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition hover:scale-105"
                          style={{
                            background: `linear-gradient(135deg, ${aiPrimaryColor} 0%, ${aiSecondaryColor} 100%)`,
                            color: textColor,
                          }}
                        >
                          <Sparkles className="w-4 h-4 inline mr-1" />
                          Build
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwitchAI(ai);
                          }}
                          className="px-3 py-2 rounded-lg text-sm font-medium transition hover:scale-105"
                          style={{
                            backgroundColor: `${aiPrimaryColor}20`,
                            color: textColor,
                          }}
                          title="Chat with this AI"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        {canConnect && (
                          <button
                            onClick={(e) => handleConnectToAI(ai, e)}
                            className="px-3 py-2 rounded-lg text-sm font-medium transition hover:scale-105"
                            style={{
                              backgroundColor: `${aiPrimaryColor}20`,
                              color: textColor,
                            }}
                            title="Connect to other AIs"
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDeleteAI(ai, e)}
                          className="px-3 py-2 rounded-lg text-sm font-medium transition hover:scale-105 text-red-600 hover:bg-red-50"
                          title="Delete AI"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="mt-4 flex items-center gap-4 text-xs" style={{ color: textSecondaryColor }}>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          <span>{ai.conversationCount || 0} conversations</span>
                        </div>
                        <span>
                          {new Date(ai.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>


      {/* Ultra Navigation Bar - Navigation Only */}
      {ais[0] && (
        <UltraNavigationBarNavOnly 
          profile={convertEntityToProfile(ais[0])}
        />
      )}
    </PremiumHeroBackground>
  );
}

